import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import {
  Plan,
  CurrentPlan,
  BillingPeriod,
  ApiResponse,
  PlansApiData,
  SubscriptionResponse,
} from "@/types/plans";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const usePlans = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null);
  const [isYearly, setIsYearly] = useState<boolean>(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const { getToken } = useAuth();

  const fetchPlans = async (): Promise<void> => {
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/subscriptions/getPlans`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch plans");

      const { data }: ApiResponse<PlansApiData> = await response.json();

      if (data?.current_plan?.name) {
        setCurrentPlan({
          name: data.current_plan.name,
          billing_period: data.current_plan.period,
          isCustom: data.current_plan.is_custom || false,
        });
        setIsYearly(data.current_plan.period === "yearly");
      }

      setPlans(data?.plans || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const processSubscription = async (plan: Plan): Promise<void> => {
    const endpoint =
      currentPlan && !currentPlan.name.toLowerCase().includes("free")
        ? "updateSubscription"
        : "createCheckoutSession";

    setLoading(plan.id);
    try {
      const token = await getToken();
      const billing_period: BillingPeriod = isYearly ? "yearly" : "monthly";

      const response = await fetch(
        `${API_BASE_URL}/api/subscriptions/${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ plan_name: `${plan.name}-${billing_period}` }),
        }
      );

      if (!response.ok) {
        if (response.status === 422) {
          const data = await response.json();
          const limitType = data?.error.split("_")[0];
          const displayType =
            limitType === "activated" ? "activated creators" : limitType;

          toast.warning(`You have too many ${displayType} for this plan. `, {
            duration: 8000,
          });
        }
        throw new Error("Failed to process subscription");
      }

      const { data }: ApiResponse<SubscriptionResponse> = await response.json();
      if (!data?.success_url) {
        window.location.reload();
      } else {
        window.location.href = data.success_url;
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  return {
    loading,
    loadingPlans,
    currentPlan,
    isYearly,
    setIsYearly,
    plans,
    processSubscription,
  };
};
